import type { EndTerms, GrantTerms } from './allowances';

/** What the operator writes, as written: nothing has a default, so a grant is always terms someone chose */
export interface Draft {
	plan: string;
	/** In mebibytes, as typed */
	bytes: string;
	sources: string;
	reason: string;
	/** A day, `YYYY-MM-DD`, or empty for no end of its own */
	ends: string;
	/** Why the allowance ends, for ending it */
	ending: string;
}

export type Field = keyof Draft;

/** A change sent and not yet decided by an answer, exactly as it was sent: the same request keeps its key until one decides it */
interface Pending {
	organization: string;
	account: string;
	operation: 'grant' | 'end';
	terms: GrantTerms | EndTerms;
	key: string;
}

/** The change on its way, to be sent again as it was or forgotten, never rebuilt from what is written now */
export interface Recovery {
	operation: 'grant' | 'end';
	terms: GrantTerms | EndTerms;
	key: string;
}

/** The bounds a grant may take, as the service says them */
export interface Bounds {
	bytes: number;
	sources: number;
	days: number;
}

/**
 * An operator's proposal for one organization's allowance: the terms as written, what is still to write and what
 * is wrong (an empty field is never said as a failure, D183), the terms a grant or an end sends, and the key of the
 * change that is on its way. The same request keeps its key until an answer decides it, so after a lost answer
 * sending it again answers what the first send made; another request takes a new key. The draft and the pending
 * change are kept in this tab's session storage, so a reload or the way to a recent sign-in loses neither. Both are
 * kept apart for each operator and organization: another operator in the tab neither reads nor overwrites them, and a
 * draft no operator owns is never handed to one. The pending change is kept exactly as it was sent (`recovery`): it
 * is only ever sent again by the operator who sent it, as it was, and no other change takes its place until an answer
 * decides it or the operator forgets it.
 *
 * Several proposals may hold one operator's storage over time (a page left and opened again while an answer was on
 * its way), so every change reads what is stored first. An answer settles the stored change only when it is still the
 * one that answer is for (its key), and clears a reason only when it is still the one sent: an earlier proposal's late
 * answer changes nothing a later one keeps. Without storage the proposal lives in memory alone.
 */
export class Proposal {
	static UNIT = 1048576;
	static EMPTY: Draft = { plan: '', bytes: '', sources: '', reason: '', ends: '', ending: '' };
	static PLAN = /^[a-z][a-z0-9-]{0,40}$/;
	static DAY = 86400000;

	#organization: string;
	#account: string;
	#storage?: Storage;
	#mint: () => string;
	#draft: Draft;
	#pending?: Pending;

	constructor(organization: string, account: string, { storage, mint }: { storage?: Storage; mint?: () => string } = {}) {
		this.#organization = organization;
		this.#account = account;
		this.#storage = storage;
		this.#mint = mint ?? (() => `op_${globalThis.crypto.randomUUID().replace(/-/g, '')}`);
		this.#draft = this.#stored().draft ?? { ...Proposal.EMPTY };
		this.#pending = this.#stored().pending;
	}

	/** The change on its way, exactly as it was sent, or undefined when none is */
	get recovery(): Recovery | undefined {
		const pending = this.#pending;
		return pending ? { operation: pending.operation, terms: structuredClone(pending.terms), key: pending.key } : undefined;
	}

	get draft(): Draft {
		return { ...this.#draft };
	}

	/** The change on its way, if any: its operation */
	get pending(): 'grant' | 'end' | undefined {
		return this.#pending?.operation;
	}

	set(field: Field, value: string): void {
		this.#draft = { ...(this.#stored().draft ?? this.#draft), [field]: value };
		this.#write(this.#name, this.#draft);
	}

	/** What a grant still needs written (`missing`) and what is written wrong (`wrong`), by field */
	check(bounds: Bounds, now = Date.now()): { missing: Field[]; wrong: Field[] } {
		const draft = this.#draft;
		const missing = (['plan', 'bytes', 'sources', 'reason'] as Field[]).filter(field => !draft[field].trim());
		const wrong: Field[] = [];
		if (draft.plan.trim() && !Proposal.PLAN.test(draft.plan.trim())) wrong.push('plan');
		const bytes = Proposal.#whole(draft.bytes);
		if (draft.bytes.trim() && (bytes === undefined || bytes * Proposal.UNIT > bounds.bytes)) wrong.push('bytes');
		const sources = Proposal.#whole(draft.sources);
		if (draft.sources.trim() && (sources === undefined || sources > bounds.sources)) wrong.push('sources');
		if (draft.reason.length > 500) wrong.push('reason');
		const ends = this.#ends();
		if (draft.ends && (ends === undefined || Date.parse(ends) <= now || Date.parse(ends) > now + bounds.days * Proposal.DAY)) wrong.push('ends');
		return { missing, wrong };
	}

	/** The terms a grant sends, or undefined while something is missing or wrong */
	grant(expected: string | null, bounds: Bounds, now = Date.now()): GrantTerms | undefined {
		const { missing, wrong } = this.check(bounds, now);
		if (missing.length || wrong.length) return undefined;
		const draft = this.#draft;
		const ends = this.#ends();
		return { plan: draft.plan.trim(), reason: draft.reason.trim(), limits: { bytes: Proposal.#whole(draft.bytes)! * Proposal.UNIT, sources: Proposal.#whole(draft.sources)! }, ...(ends ? { ends } : {}), expected };
	}

	/** The terms an end sends, or undefined while its reason is not written */
	end(expected: string): EndTerms | undefined {
		const reason = this.#draft.ending.trim();
		return reason && reason.length <= 500 ? { reason, expected } : undefined;
	}

	/**
	 * The key for this exact change: the pending one when it is the same request, a new one when none is pending
	 * @throws Error when another change is on its way: it is decided or forgotten first, never replaced
	 */
	key(operation: 'grant' | 'end', terms: GrantTerms | EndTerms): string {
		const stored = this.#stored();
		const pending = stored.readable ? stored.pending : this.#pending;
		if (pending) {
			if (pending.operation === operation && JSON.stringify(pending.terms) === JSON.stringify(terms)) {
				this.#pending = pending;
				return pending.key;
			}
			throw new Error('Another change is on its way: send it again or forget it first');
		}
		this.#pending = { organization: this.#organization, account: this.#account, operation, terms: structuredClone(terms), key: this.#mint() };
		this.#write(this.#pendingName, this.#pending);
		return this.#pending.key;
	}

	/**
	 * An answer decided the change on its way, made or refused: the next one takes a new key. A made change clears
	 * the words it was sent with, and only those: a reason written since stays
	 */
	settle(made?: 'grant' | 'end'): void {
		const own = this.#release();
		if (!own || !made) return;
		const draft = this.#stored().draft ?? this.#draft;
		const field = made === 'grant' ? 'reason' : 'ending';
		if (draft[field].trim() !== own.terms.reason) return;
		this.#draft = { ...draft, [field]: '' };
		this.#write(this.#name, this.#draft);
	}

	/** The operator stops waiting for the change on its way; what it did, if anything, is what the allowance says */
	forget(): void {
		this.#release();
	}

	/**
	 * Lets go of this proposal's change on its way. It still owns it only while the stored change is that same one (its
	 * key), or while the browser keeps nothing and the proposal lives in memory: then the stored one goes too, and the
	 * settlement may clear the words it was sent with. A stored change of another key, or none (a later proposal decided
	 * it already), leaves this proposal with nothing to settle. @returns the change it still owned, if any
	 */
	#release(): Pending | undefined {
		const own = this.#pending;
		this.#pending = undefined;
		if (!own) return undefined;
		const stored = this.#stored();
		if (stored.readable && stored.pending?.key !== own.key) return undefined;
		this.#remove(this.#pendingName);
		return own;
	}

	/** An allowance's bytes in the unit its terms are written in: "64 MiB", never a decimal "67.1 MB" */
	static size(bytes: number, language: string): string {
		const mebibytes = bytes / Proposal.UNIT;
		return `${mebibytes.toLocaleString(language, { maximumFractionDigits: 2 })} MiB`;
	}

	static #whole(value: string): number | undefined {
		const text = value.trim();
		if (!/^[0-9]{1,12}$/.test(text)) return undefined;
		const number = Number(text);
		return number >= 1 ? number : undefined;
	}

	/** The end as the moment the written day starts, in UTC, or undefined when none or not a day */
	#ends(): string | undefined {
		const day = this.#draft.ends.trim();
		if (!day) return undefined;
		const at = Date.parse(`${day}T00:00:00.000Z`);
		return /^\d{4}-\d{2}-\d{2}$/.test(day) && Number.isFinite(at) ? new Date(at).toISOString() : undefined;
	}

	/** This operator's draft for this organization: never the draft of another operator, or one no operator owns */
	get #name(): string {
		return `beyond-snapshots:allowance:${this.#organization}:draft:${this.#account}`;
	}

	get #pendingName(): string {
		return `beyond-snapshots:allowance:${this.#organization}:pending:${this.#account}`;
	}

	/** What this operator's storage holds now; `readable` false when the browser keeps nothing */
	#stored(): { readable: boolean; draft?: Draft; pending?: Pending } {
		try {
			if (!this.#storage) return { readable: false };
			const draft = this.#storage.getItem(this.#name);
			const pending = this.#storage.getItem(this.#pendingName);
			const kept = pending ? (JSON.parse(pending) as Pending) : undefined;
			const own = kept && kept.account === this.#account && kept.organization === this.#organization && kept.key && kept.terms ? kept : undefined;
			return { readable: true, draft: draft ? { ...Proposal.EMPTY, ...JSON.parse(draft) } : undefined, pending: own };
		} catch {
			return { readable: false };
		}
	}

	#write(name: string, value: unknown): void {
		try {
			this.#storage?.setItem(name, JSON.stringify(value));
		} catch {
			// A browser that keeps nothing still has the proposal for this page's life
		}
	}

	#remove(name: string): void {
		try {
			this.#storage?.removeItem(name);
		} catch {
			// Nothing kept, nothing to remove
		}
	}
}
